import { useTranslation } from "react-i18next";

import { Spacer, Typography } from "@aviary";
import { LoginOptions } from "@shared/components";
import { useFlippers } from "@shared/hooks";
import { useLocation, useNavigate, useSearchParams } from "@shared/react-router-dom";
import {
  FormWrapper,
  NewToFullscriptSignUp,
  SignInForm,
  SignInWrapper,
} from "@unauthenticated/shared/components";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import {
  SignUpModalHeader,
  SignUpModalTitle,
  StoreLogo,
  StoreOffersPill,
} from "@unauthenticated/signupSignin/components";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./RxSignInModalMain.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  ssoErrors?: string;
  storeId?: string;
}

const RxSignInModalMain = ({
  patientAttributes,
  additionalAttributes,
  ssoErrors,
  storeId,
}: Props) => {
  const { storeLogo, storeName, treatmentPlanSlug, uuid, labRecommendationId, storeSlug } =
    patientAttributes;
  const { t } = useTranslation();

  const navigate = useNavigate();

  const searchParams = useLocation().search;
  const [searchParameters] = useSearchParams();

  const isIntake = searchParameters.get("intake") === "true";

  const formVersion = "StoreSignupPage";
  const [isSupplementIntake] = useFlippers("dynamo_supplement_plan");

  const SIGN_IN_PAGE = "rx_patient_sign_in_page";
  const PAGE_VERSION = "react_with_sso";
  const FORGOT_PASSWORD_LINK = authRoutes.welcome_forgot_password(
    storeSlug,
    uuid || treatmentPlanSlug
  );

  const handleOnSignUpClick = () => {
    navigate(`${authRoutes.welcome_sign_up(storeSlug)}${searchParams}`);
  };

  const onCompleted = completedData => {
    const { redirectPath } = completedData?.auth?.userSignIn;

    if (redirectPath) {
      window.location.assign(completedData.auth.userSignIn.redirectPath);
    }
  };

  const renderIntakeSubtitle = () => {
    if (!isSupplementIntake || !isIntake) {
      return null;
    }

    return <Typography css={styles.centered}>{t(l.intake.signInDescription)}</Typography>;
  };

  const renderForm = () => {
    return (
      <FormWrapper>
        <SignInWrapper>
          <SSOErrorBox ssoErrors={ssoErrors} />
          <LoginOptions
            showMagicLinkButton
            magicLinkProps={{ storeId }}
            commonSSOProps={{
              signUpPage: SIGN_IN_PAGE,
              pageVersion: PAGE_VERSION,
              treatmentPlanSlug,
              labRecommendationId,
              storeSlug,
              uuid,
              additionalAttributes,
            }}
          />
          <SignInForm
            additionalAttributes={{ formVersion, ...additionalAttributes, storeId }}
            forgotPasswordLink={FORGOT_PASSWORD_LINK}
            onCompleted={onCompleted}
            isIntake={isIntake}
          />
          <Spacer height="doubleHalf" />
          <NewToFullscriptSignUp userType="patient" customSignUpAction={handleOnSignUpClick} />
        </SignInWrapper>
      </FormWrapper>
    );
  };

  return (
    <>
      <SignUpModalHeader>
        <StoreLogo storeLogo={storeLogo} storeName={storeName} />
        <SignUpModalTitle title={storeName} />
        <StoreOffersPill patientAttributes={patientAttributes} />
        {renderIntakeSubtitle()}
      </SignUpModalHeader>
      <Spacer height="double" />
      {renderForm()}
    </>
  );
};

export { RxSignInModalMain };
